/**
 * MechanicWalletBoard — iter138
 * ---------------------------------------------------------------------------
 * SM/FM workshop wallet board. Hard-locked column order:
 *
 *   Booking → Dropped-off → [Mechanic lanes — drag to reorder] →
 *     Waiting for Parts → Sublet → Break → SM Review → Completed
 *
 * Mechanic lane headers expose Clock-In / Break / Clock-Off override
 * buttons (SM/FM/SA only) that hit the workday endpoints with
 * ?target_mechanic_id=<id>. Going on break auto-moves the mechanic's
 * `in_progress` JC to the Break column; coming back restores it.
 *
 * Drag flows:
 *   • Tile WITHIN a lane          → priority reorder
 *   • Tile ACROSS mechanic lanes  → re-assign
 *   • Tile → static status lane   → status change
 *   • Mechanic-column header DnD  → persisted lane order (localStorage)
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { withSessionHash } from '@/lib/authFetch';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  ClipboardCheck, AlertTriangle, PauseCircle, PlayCircle,
  CalendarClock, CheckCircle2, Wrench, Inbox, Coffee,
  ChevronDown, GripVertical, LogIn, LogOut, Play, FileCheck,
  FileText,
  ArrowUp, ArrowDown, Search, X, Clock,
} from 'lucide-react';
import JobCardLabourLive from './JobCardLabourLive';
import JobCardPerMechanicHours from './JobCardPerMechanicHours';
import JobCardMenu from './JobCardMenu';
import { SERVICE_TYPE_LABELS } from './JobCardDetailModal';
import { formatDeliveryDueLabel } from '@/lib/melbourneTime';
import BulkBookingSmsModal from '@/components/BulkBookingSmsModal';
import { useCommsGate } from '@/components/CommsUpgradeDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────────  status palette  ────────────────────────── */
const STATUS_META = {
  quote:             { Icon: FileText,      color: 'border-indigo-300 bg-indigo-50 text-indigo-800',   label: 'Quote' },
  pickup:            { Icon: ClipboardCheck, color: 'border-violet-300 bg-violet-50 text-violet-800',  label: 'Dropped-off' },
  in_progress:       { Icon: PlayCircle,    color: 'border-blue-300 bg-blue-50 text-blue-800',          label: 'In Progress' },
  break:             { Icon: Coffee,        color: 'border-amber-300 bg-amber-50 text-amber-800',      label: 'Break' },
  waiting_for_parts: { Icon: PauseCircle,   color: 'border-amber-300 bg-amber-50 text-amber-800',      label: 'Waiting Parts' },
  blocked:           { Icon: AlertTriangle, color: 'border-rose-300 bg-rose-50 text-rose-800',         label: 'Sublet' },
  sm_review:         { Icon: ClipboardCheck,color: 'border-indigo-300 bg-indigo-50 text-indigo-800',   label: 'SM Review' },
  booking:           { Icon: CalendarClock, color: 'border-sky-300 bg-sky-50 text-sky-800',            label: 'Booking' },
  no_show:           { Icon: AlertTriangle, color: 'border-orange-300 bg-orange-50 text-orange-800',   label: 'No Show' },
  completed:         { Icon: CheckCircle2,  color: 'border-emerald-300 bg-emerald-50 text-emerald-800',label: 'Completed' },
};

// In the wallet, mechanic lanes only contain JCs that the mechanic is
// actively responsible for HERE & NOW. waiting_for_parts / blocked / break
// have their own lanes so SM sees at a glance how many are paused.
const MECH_LANE_STATUSES = ['pickup', 'in_progress'];
// PERF (iter230) — max tiles mounted per lane before "Show more".
const WALLET_LANE_PAGE = 30;

/* Droppable lane id helpers */
const DROP_QUOTE          = 'wallet-quote';
const DROP_BOOKING        = 'wallet-booking';
const DROP_PICKUP_UNASSGN = 'wallet-pickup-unassigned';
const DROP_WAITING_PARTS  = 'wallet-waiting-parts';
const DROP_SUBLET         = 'wallet-sublet';
const DROP_BREAK          = 'wallet-break';
const DROP_REVIEW         = 'wallet-sm-review';
const DROP_COMPLETED      = 'wallet-completed';
const dropMechId = (uid) => `wallet-mech-${uid}`;
const parseMechFromDropId = (id) => id.startsWith('wallet-mech-')
  ? id.slice('wallet-mech-'.length) : null;

// localStorage key for persisted mechanic column order (per browser/SM).
const COL_ORDER_KEY = 'hk_wallet_mech_order_v1';

/* ─────────────────────────  helpers  ───────────────────────────────── */
function flattenColumns(columns) {
  const out = [];
  (columns || []).forEach((col) => (col.cards || []).forEach((c) => {
    const quoted_hours = (c.line_items || []).reduce(
      (s, li) => s + (Number(li.quoted_labour_hours) || 0), 0);
    out.push({ ...c, status: c.status || col.status, quoted_hours });
  }));
  return out;
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).map((s) => s[0]).join('').slice(0, 2).toUpperCase();
}

function mechanicLookup(mechanics) {
  const map = new Map();
  (mechanics || []).forEach((m) => map.set(m.user_id, m));
  return map;
}

/* Load + save persisted mechanic column order. Stored as an array of
 * user_ids in display order. Unknown ids are dropped on load. */
function loadSavedOrder() {
  try {
    const raw = localStorage.getItem(COL_ORDER_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch (_e) { return []; }
}
function saveOrder(ids) {
  try { localStorage.setItem(COL_ORDER_KEY, JSON.stringify(ids || [])); }
  catch (_e) { /* localStorage may be blocked — silent fallback */ }
}

function groupCards(cards, mechanics, mechOrder) {
  const mechLookup = mechanicLookup(mechanics);
  const lanes = new Map();
  (mechanics || []).forEach((m) => lanes.set(m.user_id, {
    mechanic: m, cards: [],
  }));

  const quote = [];
  const booking = [];
  const pickupUnassigned = [];
  const waitingParts = [];
  const sublet = [];
  const breakLane = [];
  const review = [];
  const completed = [];

  for (const c of cards) {
    if (c.status === 'quote') { quote.push(c); continue; }
    if (c.status === 'booking' || c.status === 'no_show') { booking.push(c); continue; }
    if (c.status === 'sm_review') { review.push(c); continue; }
    if (c.status === 'completed') { completed.push(c); continue; }
    if (c.status === 'waiting_for_parts') { waitingParts.push(c); continue; }
    if (c.status === 'blocked') { sublet.push(c); continue; }
    if (c.status === 'break') { breakLane.push(c); continue; }
    if (!MECH_LANE_STATUSES.includes(c.status)) continue;
    if (!c.assigned_to) { pickupUnassigned.push(c); continue; }
    if (!mechLookup.has(c.assigned_to)) {
      pickupUnassigned.push({
        ...c,
        assigned_mechanic_name: null,
        _orphan_mechanic: true,
      });
      continue;
    }
    const lane = lanes.get(c.assigned_to);
    lane.cards.push(c);
  }

  const sortByRank = (a, b) => {
    const ra = (a.priority_rank ?? null);
    const rb = (b.priority_rank ?? null);
    if (ra === null && rb === null) return 0;
    if (ra === null) return 1;
    if (rb === null) return -1;
    return ra - rb;
  };
  booking.sort(sortByRank);
  pickupUnassigned.sort(sortByRank);
  waitingParts.sort(sortByRank);
  sublet.sort(sortByRank);
  breakLane.sort(sortByRank);
  review.sort(sortByRank);
  completed.sort(sortByRank);
  quote.sort((a, b) => {
    // newest quote first so the latest customer ringback surfaces at top
    const at = a.created_at || a.updated_at || '';
    const bt = b.created_at || b.updated_at || '';
    return bt.localeCompare(at);
  });
  lanes.forEach((lane) => lane.cards.sort(sortByRank));

  // Mechanic column order — saved positions first, unknowns at the end
  // in alphabetical order. Order is STABLE across renders so columns
  // don't jump when mechanics dropdown reorders.
  const nameOf = (m) => (
    m?.display_name
    || m?.name
    || `${m?.first_name || ''} ${m?.last_name || ''}`.trim()
    || m?.email
    || m?.user_id
    || ''
  ).toLowerCase();

  const allMechIds = (mechanics || []).map((m) => m.user_id);
  const knownSaved = (mechOrder || []).filter((id) => allMechIds.includes(id));
  const remaining = allMechIds
    .filter((id) => !knownSaved.includes(id))
    .sort((a, b) => {
      const an = nameOf(mechLookup.get(a));
      const bn = nameOf(mechLookup.get(b));
      if (an < bn) return -1;
      if (an > bn) return 1;
      return a < b ? -1 : a > b ? 1 : 0;
    });
  const orderedIds = [...knownSaved, ...remaining];
  const laneArray = orderedIds
    .map((id) => lanes.get(id))
    .filter(Boolean);

  return {
    quote, booking, pickupUnassigned, waitingParts, sublet, breakLane,
    lanes: laneArray, review, completed, mechLookup,
  };
}

/* ─────────────────────────  tile  ──────────────────────────────────── */
function fmtPickupDate(c) {
  const iso = c.scheduled_date || c.booked_datetime;
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch (_e) { return null; }
}

const SERVICE_TYPE_META = {
  service:    { cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  rwc:        { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  tyres:      { cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  tyre:       { cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  aircon:     { cls: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  ac:         { cls: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  brakes:     { cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  suspension: { cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  electrical: { cls: 'bg-violet-100 text-violet-800 border-violet-200' },
  other:      { cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

function serviceTypeLabel(key) {
  if (!key) return null;
  const lower = String(key).toLowerCase();
  if (SERVICE_TYPE_LABELS[lower]) return SERVICE_TYPE_LABELS[lower];
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function serviceTypesFor(card) {
  const set = new Set();
  if (card.service_type) set.add(String(card.service_type).toLowerCase());
  if (Array.isArray(card.service_types)) {
    card.service_types.forEach((s) => s && set.add(String(s).toLowerCase()));
  }
  return [...set];
}

const STATUS_OPTIONS = [
  { value: 'pickup', label: 'Dropped-off' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'break', label: 'Break' },
  { value: 'waiting_for_parts', label: 'Waiting Parts' },
  { value: 'blocked', label: 'Sublet' },
  { value: 'sm_review', label: 'SM Review' },
  { value: 'completed', label: 'Completed' },
];

function JCTileInner({
  card, onClick, showMechanic, mechName, indexBadge, labourRate, isDragging,
  onPartsToggle, partsBusyId, onStatusChange, statusBusyId, canManage,
  user, mechanics, onFetchMechanics, onUpdatePriority, onUpdateDeliveryDue, onAssignMechanic,
  onDelete, onCreateInvoice,
}) {
  const meta = STATUS_META[card.status] || STATUS_META.pickup;
  const Icon = meta.Icon;
  const makeModel = [card.vehicle_make, card.vehicle_model].filter(Boolean).join(' ');
  const pickupDate = fmtPickupDate(card);

  // iter179 — Total ESTIMATED labour hours across ALL tasks on the JC
  // (completed + pending). Always visible on the wallet tile so the SM
  // sees a dropped-off JC's allocated hours BEFORE the mechanic clocks on
  // (JobCardLabourLive hides its pill until time is logged) and so
  // completed tasks never shrink the estimate. Mirrors the Status view
  // hours badge.
  const estHours = (card.line_items || []).reduce(
    (s, li) => s + (Number(li.quoted_labour_hours) || 0), 0);
  const estHoursLabel = estHours > 0
    ? (Number.isInteger(estHours) ? `${estHours}h` : `${estHours.toFixed(1)}h`)
    : '';

  const showPartsPill = ['pickup', 'in_progress', 'break', 'waiting_for_parts', 'blocked'].includes(card.status);
  const partsBusy = partsBusyId === card.job_card_id;
  const statusBusy = statusBusyId === card.job_card_id;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(card)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(card);
        }
      }}
      data-testid={`wallet-jc-tile-${card.job_card_id}`}
      className={`
        text-left rounded-lg border ${meta.color}
        px-2.5 py-2 w-full mb-2 cursor-pointer
        ${isDragging ? 'shadow-xl ring-2 ring-blue-400' : 'hover:shadow-md hover:-translate-y-0.5'}
        transition-all
        focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400
      `}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        {canManage && onStatusChange ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={statusBusy}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                data-testid={`wallet-status-trigger-${card.job_card_id}`}
                title="Click to change status"
                className={`
                  inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold
                  px-1.5 py-0.5 rounded border cursor-pointer
                  ${statusBusy ? 'opacity-50 cursor-wait' : 'hover:bg-white/60'}
                  ${meta.color}
                `}
              >
                <Icon className="h-3 w-3" />
                {meta.label}
                <ChevronDown className="h-2.5 w-2.5 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              onClick={(e) => e.stopPropagation()}
              data-testid={`wallet-status-menu-${card.job_card_id}`}
            >
              <DropdownMenuLabel className="text-[11px]">
                Move {card.vehicle_rego || 'job'} to…
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {STATUS_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  disabled={opt.value === card.status || statusBusy}
                  onSelect={(e) => {
                    e?.preventDefault?.();
                    onStatusChange(card, opt.value);
                  }}
                  data-testid={`wallet-status-opt-${card.job_card_id}-${opt.value}`}
                >
                  {opt.label}
                  {opt.value === card.status && (
                    <span className="ml-auto text-[10px] text-slate-400">current</span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold">
            <Icon className="h-3.5 w-3.5 shrink-0" />
            {meta.label}
          </span>
        )}

        {showPartsPill && (
          <button
            type="button"
            disabled={partsBusy || !onPartsToggle}
            onClick={(e) => {
              e.stopPropagation();
              if (onPartsToggle) onPartsToggle(card);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            data-testid={`wallet-parts-${card.job_card_id}`}
            title={card.parts_ordered
              ? 'Parts Ordered (PO) — click to mark as not ordered'
              : 'Order Parts (OP) — click to flag parts as ordered with supplier'}
            className={`
              ml-1 inline-flex items-center justify-center text-[10px] font-extrabold uppercase
              w-7 h-5 rounded border transition-colors leading-none
              ${partsBusy ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
              ${card.parts_ordered
                ? 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600'
                : 'bg-rose-500 text-white border-rose-600 hover:bg-rose-600 animate-pulse'}
            `}
          >
            {card.parts_ordered ? 'PO' : 'OP'}
          </button>
        )}
        {card.on_break && card.status !== 'break' && (
          <span
            data-testid={`wallet-on-break-${card.job_card_id}`}
            title="Mechanic is on break — JC clock is paused"
            className="ml-1 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase text-amber-900 bg-amber-200 border border-amber-400 rounded px-1.5 py-0.5 leading-none animate-pulse"
          >
            <Coffee className="h-2.5 w-2.5" />
            On Break
          </span>
        )}
        {canManage && onUpdatePriority && (
          <div
            className="ml-1"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <JobCardMenu
              card={card}
              user={user}
              mechanics={mechanics}
              onFetchMechanics={onFetchMechanics}
              onUpdatePriority={onUpdatePriority}
              onUpdateDeliveryDue={onUpdateDeliveryDue}
              onAssignMechanic={onAssignMechanic}
              onDelete={onDelete}
              onCreateInvoice={onCreateInvoice}
            />
          </div>
        )}
        <div className="ml-auto flex flex-col items-end gap-1 shrink-0">
          {Number.isFinite(indexBadge) && (
            <span
              className="text-[10px] font-extrabold bg-slate-900 text-white px-1.5 py-0.5 rounded"
              data-testid={`wallet-rank-${card.job_card_id}`}
              title="Drag tile up/down to re-order priority"
            >
              #{indexBadge}
            </span>
          )}
          {(() => {
            const types = serviceTypesFor(card);
            if (!types.length) return null;
            return (
              <div
                className="flex flex-col items-end gap-0.5"
                data-testid={`wallet-service-types-${card.job_card_id}`}
              >
                {types.map((t) => {
                  const sMeta = SERVICE_TYPE_META[t] || SERVICE_TYPE_META.other;
                  return (
                    <span
                      key={t}
                      className={`inline-block text-[10px] font-semibold tracking-tight border rounded px-1.5 py-0.5 leading-none ${sMeta.cls}`}
                      title={`Service category: ${serviceTypeLabel(t)}`}
                      data-testid={`wallet-svc-${card.job_card_id}-${t}`}
                    >
                      {serviceTypeLabel(t)}
                    </span>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      <p
        className="text-base font-extrabold leading-tight text-slate-900 truncate uppercase tracking-wide"
        data-testid={`wallet-rego-${card.job_card_id}`}
      >
        {card.vehicle_rego || 'NO REGO'}
      </p>
      {/* iter164 — Ready-to-Quote / Invoice-Created pill next to rego.
          - Mechanic sets `ready_to_quote=true` from inside the JC modal.
          - Backend clears the flag + sets `invoice_id` on invoice-create.
          - So the pill state flips: orange READY TO QUOTE → emerald
            INVOICE CREATED. Matches the PO / OP visual pattern.
       */}
      {card.ready_to_quote && !card.invoice_id && (
        <span
          className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-orange-900 bg-orange-200 border border-orange-400 rounded px-1.5 py-0.5 leading-none"
          data-testid={`wallet-ready-to-quote-${card.job_card_id}`}
          title="Mechanic marked this JC ready to quote — click the tile to review findings and create an invoice"
        >
          <FileCheck className="h-2.5 w-2.5" />
          Ready to Quote
        </span>
      )}
      {card.invoice_id && (
        <span
          className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-emerald-900 bg-emerald-100 border border-emerald-400 rounded px-1.5 py-0.5 leading-none"
          data-testid={`wallet-invoice-created-badge-${card.job_card_id}`}
          title="Invoice created — click the tile to view it"
        >
          <FileCheck className="h-2.5 w-2.5" />
          Invoice Created
        </span>
      )}
      {makeModel && (
        <p
          className="text-base font-extrabold leading-tight text-slate-800 truncate uppercase tracking-wide"
          data-testid={`wallet-makemodel-${card.job_card_id}`}
        >
          {makeModel}
        </p>
      )}

      <p className="text-[11px] font-medium text-slate-800 truncate mt-1">
        {card.customer_name || '—'}
      </p>
      <div className="flex items-baseline justify-between gap-2">
        {card.customer_phone && (
          <p className="text-[11px] text-slate-600 truncate tabular-nums">
            {card.customer_phone}
          </p>
        )}
        {/* iter138.9v — DD By next to customer phone (right column).
            Replaces the bottom priority badge for the wallet view per
            user request: "in wallet view below category and next to
            name and number, use these row to show DD By and date time
            picked in right column". */}
        {card.delivery_due_at && (() => {
          const label = formatDeliveryDueLabel(card.delivery_due_at);
          if (!label) return null;
          return (
            <span
              className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 whitespace-nowrap shrink-0"
              data-testid={`wallet-jc-delivery-due-${card.job_card_id}`}
              title="Delivery due — when the car must leave the workshop"
            >
              DD {label}
            </span>
          );
        })()}
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`wallet-labour-${card.job_card_id}`}>
        {estHoursLabel && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-blue-200 bg-blue-50 text-blue-700 rounded-full text-[10px] font-semibold"
            data-testid={`wallet-est-hours-${card.job_card_id}`}
            title="Total estimated labour hours across ALL tasks on this job card (including completed tasks)"
          >
            <Clock className="h-2.5 w-2.5" />
            {estHoursLabel} est
          </span>
        )}
        <JobCardLabourLive card={card} compact labourRate={labourRate} />
        {/* iter138.9y — Per-mechanic breakdown rows: makes visible WHO
            produced HOW MUCH on this JC when 2+ mechanics contributed. */}
        <JobCardPerMechanicHours card={card} mechanics={mechanics} />
      </div>

      {/* iter138.9ag — Invoice-created pill on the wallet tile. Mirrors
          the Status view (JobCardItem.jsx) but RBAC-gated: visible only
          to Super Admin + Service Manager so they can jump from the
          tile straight to the invoice. FM, mechanics, and any other
          role do NOT see it (per user request). */}
      {card.invoice_id && ['super_admin', 'admin', 'service_manager'].includes(user?.role) && (
        <div className="mt-1.5 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <a
            href={withSessionHash(`/invoices/${card.invoice_id}`)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-full px-1.5 py-0.5 hover:bg-emerald-100 w-fit"
            data-testid={`wallet-invoice-link-${card.job_card_id}`}
            title="Open the linked invoice in a new tab"
          >
            <FileCheck className="h-3 w-3" />
            Invoice
          </a>
          <button
            type="button"
            onClick={async (e) => {
              e.preventDefault(); e.stopPropagation();
              try {
                const r = await fetch(`${BACKEND_URL}/api/invoices/${card.invoice_id}/pdf`, { credentials: 'include' });
                if (!r.ok) throw new Error();
                window.open(URL.createObjectURL(await r.blob()), '_blank');
              } catch (_e) { toast.error("Couldn't open the invoice PDF — try again"); }
            }}
            className="inline-flex items-center text-[10px] font-bold text-white bg-emerald-600 border border-emerald-700 rounded-full px-1.5 py-0.5 hover:bg-emerald-700"
            data-testid={`wallet-invoice-pdf-${card.job_card_id}`}
            title="Open the invoice PDF"
          >
            PDF
          </button>
        </div>
      )}

      {pickupDate && (
        <p className="text-[10px] text-slate-500 truncate mt-1">
          Dropped: {pickupDate}
        </p>
      )}

      {showMechanic && mechName && (
        <p className="mt-1 text-[10px] text-slate-700 truncate flex items-center gap-1 font-medium">
          <Wrench className="h-2.5 w-2.5 shrink-0" />
          <span className="truncate">{mechName}</span>
        </p>
      )}
    </div>
  );
}

/* iter164 — Memoise the tile so a state change on the parent
   MechanicWalletBoard (errMsg, statusBusyId, partsBusyId, selection,
   bulkSmsOpen, workdayMap tick every 15s, drag-in-flight ref) does NOT
   re-render every single JC tile. Field-level compare on the card
   object: refetches produce NEW card object identities, but the tile
   only re-renders when a VISIBLE field actually changed. Reduces
   re-render count during dnd from O(N × ticks) to O(1) and eliminates
   the "snap-back" flicker after each refetch.

   ⚠️ IMPORTANT: `_CARD_COMPARE_FIELDS` below is a HARDCODED whitelist
   of card fields the tile visually depends on. If you add a new field
   render inside JCTileInner (or any component it composes), you MUST
   add that field name here — otherwise edits to the field won't cause
   the tile to re-render and you'll see stale UI until the next
   mount/unmount cycle. Grep for `card\\.` inside JCTileInner + its
   children (WalletCard, PartsPill, ReadyToQuotePill, etc.) whenever
   you extend the tile.
 */
const _CARD_COMPARE_FIELDS = [
  'job_card_id', 'status', 'vehicle_rego', 'vehicle_make', 'vehicle_model',
  'customer_name', 'customer_phone', 'assigned_to', 'assigned_mechanic_name',
  'priority_rank', 'scheduled_date', 'booked_datetime', 'delivery_due_at',
  'parts_ordered', 'ready_to_quote', 'invoice_id', 'on_break',
  'service_type', 'escalation_status', 'kanban_anchor_date',
];
function jcTileEqual(prev, next) {
  if (prev.isDragging !== next.isDragging) return false;
  if (prev.indexBadge !== next.indexBadge) return false;
  if (prev.showMechanic !== next.showMechanic) return false;
  if (prev.mechName !== next.mechName) return false;
  if (prev.labourRate !== next.labourRate) return false;
  if (prev.canManage !== next.canManage) return false;
  const a = prev.card || {}; const b = next.card || {};
  for (const k of _CARD_COMPARE_FIELDS) {
    if (a[k] !== b[k]) return false;
  }
  // Compare arrays by length + last entry to catch line_items / time_log
  // changes without deep-equal cost.
  const aLi = a.line_items || []; const bLi = b.line_items || [];
  if (aLi.length !== bLi.length) return false;
  const aTl = a.time_log || []; const bTl = b.time_log || [];
  if (aTl.length !== bTl.length) return false;
  // Open session state — "live clock" pill needs to react to end.
  const aOpen = aTl.length && !aTl[aTl.length - 1].ended_at;
  const bOpen = bTl.length && !bTl[bTl.length - 1].ended_at;
  if (aOpen !== bOpen) return false;
  const aSvc = (a.service_types || []).join(','); const bSvc = (b.service_types || []).join(',');
  if (aSvc !== bSvc) return false;
  // Busy flags only matter for THIS card's id — skip re-render otherwise.
  const id = b.job_card_id;
  const prevPartsBusy = prev.partsBusyId === id;
  const nextPartsBusy = next.partsBusyId === id;
  if (prevPartsBusy !== nextPartsBusy) return false;
  const prevStatusBusy = prev.statusBusyId === id;
  const nextStatusBusy = next.statusBusyId === id;
  if (prevStatusBusy !== nextStatusBusy) return false;
  return true;
}
const JCTile = React.memo(JCTileInner, jcTileEqual);

function DraggableTile({ card, index, draggableId, ...rest }) {
  return (
    <Draggable draggableId={draggableId} index={index} disableInteractiveElementBlocking>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={provided.draggableProps.style}
        >
          <JCTile card={card} isDragging={snapshot.isDragging} indexBadge={index + 1} {...rest} />
        </div>
      )}
    </Draggable>
  );
}

/* ─────────────────────────  Mechanic Clock Controls  ───────────────── */
/** SM/FM/SA-only inline override row in each mechanic lane header.
 * Shows the mechanic's live workday state (Off / On Clock / On Break) and
 * lets a manager force-clock-in / break / clock-off for them. */
function MechanicClockControls({ mechanicId, workday, busy, onAction }) {
  const state = workday?.state || 'off';
  const isOff = state === 'off';
  const onClock = state === 'on_clock';
  const onBreak = state === 'on_break';

  const stateLabel = onBreak ? 'On break' : onClock ? 'On clock' : 'Off';
  const stateColor = onBreak
    ? 'bg-amber-100 text-amber-800 border-amber-300'
    : onClock
      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
      : 'bg-slate-100 text-slate-600 border-slate-300';

  const btnBase = 'inline-flex items-center justify-center gap-1 text-[10px] font-semibold px-1.5 py-1 rounded border leading-none transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="flex items-center gap-1 mt-1.5 flex-wrap" data-testid={`wallet-clock-controls-${mechanicId}`}>
      <span
        className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border ${stateColor}`}
        data-testid={`wallet-clock-state-${mechanicId}`}
      >
        {stateLabel}
      </span>
      {isOff && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onAction(mechanicId, 'clock-in')}
          title="Clock-In this mechanic"
          data-testid={`wallet-clock-in-${mechanicId}`}
          className={`${btnBase} bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100`}
        >
          <LogIn className="h-3 w-3" />
          In
        </button>
      )}
      {onClock && (
        <>
          <button
            type="button"
            disabled={busy}
            onClick={() => onAction(mechanicId, 'break-start')}
            title="Send mechanic on Break (auto-moves their active JC)"
            data-testid={`wallet-break-start-${mechanicId}`}
            className={`${btnBase} bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100`}
          >
            <Coffee className="h-3 w-3" />
            Break
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onAction(mechanicId, 'clock-off')}
            title="Clock-Off this mechanic"
            data-testid={`wallet-clock-off-${mechanicId}`}
            className={`${btnBase} bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100`}
          >
            <LogOut className="h-3 w-3" />
            Off
          </button>
        </>
      )}
      {onBreak && (
        <>
          <button
            type="button"
            disabled={busy}
            onClick={() => onAction(mechanicId, 'break-end')}
            title="End the break (auto-resumes their JC)"
            data-testid={`wallet-break-end-${mechanicId}`}
            className={`${btnBase} bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100`}
          >
            <Play className="h-3 w-3" />
            Resume
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onAction(mechanicId, 'clock-off')}
            title="Clock-Off this mechanic"
            data-testid={`wallet-clock-off-${mechanicId}`}
            className={`${btnBase} bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100`}
          >
            <LogOut className="h-3 w-3" />
            Off
          </button>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────  lane shells  ───────────────────────────── */
function StaticLane({
  title, subtitle, icon: Icon, accent, dropId, items, emptyLabel,
  onCardClick, labourRate, showMechanic, mechLookup, hoursTotal, testId,
  onPartsToggle, partsBusyId, onStatusChange, statusBusyId, canManage,
  user, mechanics, onFetchMechanics, onUpdatePriority, onUpdateDeliveryDue, onAssignMechanic,
  onDelete, onCreateInvoice,
  // iter141 — bulk-SMS opt-in props
  selectableForBulkSms = false, selectedIds, onToggleSelect, onSelectAll,
  onClearSelection, onSendBulkSms,
  // iter149 — per-section search + sort (Wallet parity with Status view)
  sortDir = 'desc', onToggleSort,
  search = '', onSearchChange,
}) {
  // iter149 — Filter + sort items. Search matches customer name / phone /
  // rego / vehicle make/model / JC number (same haystack as Status view
  // in JobCards.jsx displayCardsFor). Sort by "best" date field per lane:
  //   • Completed → completed_at (today's completions jump to top)
  //   • Booking/Pickup → scheduled_date
  //   • SM Review → reviewed_at
  //   • Quote → updated_at
  const displayItems = React.useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    let arr = items || [];
    if (q) {
      arr = arr.filter((c) => {
        const hay = [
          c.customer_name, c.customer_phone, c.customer_mobile, c.customer_email,
          c.vehicle_rego, c.vehicle_make, c.vehicle_model, c.job_card_number,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    const getDate = (card) => {
      const t = card.status;
      let raw;
      if (t === 'completed' || t === 'delivered') raw = card.completed_at || card.updated_at || card.created_at;
      else if (t === 'booking' || t === 'no_show' || t === 'drop_off' || t === 'pickup') raw = card.scheduled_date || card.updated_at || card.created_at;
      else if (t === 'sm_review') raw = card.reviewed_at || card.updated_at || card.created_at;
      else raw = card.updated_at || card.created_at || card.kanban_anchor_date;
      return raw ? new Date(raw).getTime() : 0;
    };
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...arr].sort((a, b) => (getDate(a) - getDate(b)) * dir);
  }, [items, search, sortDir]);

  // PERF (iter230) — Windowed rendering. Big piles (Quotes 200+, Pickup
  // 200+) used to mount EVERY tile inside the DragDropContext which made
  // the whole Wallet view crawl. Render a prefix + "Show more" instead —
  // drag-and-drop indices stay valid because the visible list is a strict
  // prefix of the full display array.
  const [visibleCount, setVisibleCount] = React.useState(WALLET_LANE_PAGE);
  React.useEffect(() => { setVisibleCount(WALLET_LANE_PAGE); }, [search, sortDir]);
  const visibleItems = displayItems.length > visibleCount
    ? displayItems.slice(0, visibleCount)
    : displayItems;
  const hiddenCount = displayItems.length - visibleItems.length;

  const selectedCount = selectableForBulkSms && selectedIds ? selectedIds.size : 0;
  const allSelectedOnPage = selectableForBulkSms && selectedIds
    && displayItems.length > 0 && displayItems.every((c) => selectedIds.has(c.job_card_id));
  return (
    <div
      className={`flex flex-col w-64 shrink-0 rounded-lg border ${accent} bg-white`}
      data-testid={testId}
    >
      <div className="px-3 py-2 border-b border-slate-200 sticky top-0 bg-inherit rounded-t-lg">
        <div className="flex items-center gap-2 mb-0.5">
          {Icon && <Icon className="h-4 w-4 shrink-0 text-slate-700" />}
          <h4 className="text-sm font-bold text-slate-900 truncate flex-1">{title}</h4>
          {/* iter149 — Sort arrow (asc/desc). Default desc. */}
          {onToggleSort && (
            <button
              type="button"
              onClick={onToggleSort}
              className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-slate-600 hover:text-slate-900 bg-white/70 hover:bg-white px-1 py-0.5 rounded border border-white/60"
              title={`Sort by date ${sortDir === 'asc' ? 'ascending' : 'descending'} (click to toggle)`}
              data-testid={`${testId}-sort`}
            >
              {sortDir === 'asc'
                ? <ArrowUp className="h-3 w-3" />
                : <ArrowDown className="h-3 w-3" />}
            </button>
          )}
          <Badge variant="outline" className="text-[10px] shrink-0">
            {displayItems.length !== items.length ? `${displayItems.length}/${items.length}` : items.length}
          </Badge>
        </div>
        {subtitle && <p className="text-[11px] text-slate-500 truncate">{subtitle}</p>}
        {hoursTotal > 0 && (
          <p className="text-[10px] font-mono text-slate-600">{hoursTotal.toFixed(1)}h quoted</p>
        )}
        {/* iter149 — Per-section search bar (Wallet). */}
        {onSearchChange && (
          <div className="mt-1.5 flex items-center gap-1 px-1.5 py-1 bg-white rounded border border-slate-200">
            <Search className="h-3 w-3 text-slate-400 flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Name / phone / rego"
              className="flex-1 min-w-0 text-[10px] bg-transparent outline-none placeholder:text-slate-400 text-slate-700"
              data-testid={`${testId}-search`}
            />
            {search && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="text-slate-400 hover:text-slate-700"
                data-testid={`${testId}-search-clear`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
        {selectableForBulkSms && displayItems.length > 0 && (
          <div
            className="mt-1.5 flex items-center gap-1.5 text-[11px]"
            data-testid={`${testId}-bulk-sms-bar`}
          >
            <label
              className="inline-flex items-center gap-1 cursor-pointer select-none text-slate-700 hover:text-slate-900"
              title="Select all in this column"
            >
              <input
                type="checkbox"
                checked={!!allSelectedOnPage}
                onChange={(e) => {
                  if (e.target.checked) onSelectAll?.(displayItems);
                  else onClearSelection?.();
                }}
                className="h-3.5 w-3.5"
                data-testid={`${testId}-select-all`}
              />
              {allSelectedOnPage ? 'All' : `${selectedCount}/${displayItems.length}`}
            </label>
            <button
              type="button"
              disabled={selectedCount === 0}
              onClick={onSendBulkSms}
              className={`ml-auto px-2 py-0.5 rounded text-[10px] font-semibold border transition ${
                selectedCount === 0
                  ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                  : 'bg-indigo-600 text-white border-indigo-700 hover:bg-indigo-700'
              }`}
              data-testid={`${testId}-send-bulk-sms`}
            >
              Send SMS ({selectedCount})
            </button>
          </div>
        )}
      </div>
      <Droppable droppableId={dropId} type="WALLET_JC">
        {(dProvided, snap) => (
          <div
            ref={dProvided.innerRef}
            {...dProvided.droppableProps}
            className={`flex-1 p-2 overflow-y-auto max-h-[calc(100vh-280px)] min-h-[200px] ${snap.isDraggingOver ? 'bg-blue-50/40' : ''}`}
          >
            {displayItems.length === 0 ? (
              <p className="text-[11px] text-slate-400 italic text-center py-3">
                {(search || '').trim() ? 'No matches' : emptyLabel}
              </p>
            ) : (
              visibleItems.map((c, idx) => (
                <div
                  key={c.job_card_id}
                  className={selectableForBulkSms && selectedIds?.has(c.job_card_id)
                    ? 'relative ring-2 ring-indigo-300 ring-offset-1 rounded-md mb-1'
                    : 'relative'}
                >
                  {selectableForBulkSms && (
                    <label
                      onClick={(e) => e.stopPropagation()}
                      className="absolute z-10 top-2 left-2 inline-flex items-center justify-center h-5 w-5 rounded bg-white border border-slate-300 shadow-sm cursor-pointer hover:border-indigo-500"
                      title="Select for bulk SMS"
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedIds?.has(c.job_card_id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          onToggleSelect?.(c.job_card_id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-3.5 w-3.5 cursor-pointer"
                        data-testid={`${testId}-checkbox-${c.job_card_id}`}
                      />
                    </label>
                  )}
                  <DraggableTile
                    card={c}
                    index={idx}
                    draggableId={c.job_card_id}
                    onClick={onCardClick}
                    showMechanic={showMechanic}
                    mechName={showMechanic ? (mechLookup?.get(c.assigned_to)?.name || c.assigned_mechanic_name) : null}
                    labourRate={labourRate}
                    onPartsToggle={onPartsToggle}
                    partsBusyId={partsBusyId}
                    onStatusChange={onStatusChange}
                    statusBusyId={statusBusyId}
                    canManage={canManage}
                    user={user}
                    mechanics={mechanics}
                    onFetchMechanics={onFetchMechanics}
                    onUpdatePriority={onUpdatePriority}
                    onUpdateDeliveryDue={onUpdateDeliveryDue}
                    onAssignMechanic={onAssignMechanic}
                    onDelete={onDelete}
                    onCreateInvoice={onCreateInvoice}
                  />
                </div>
              ))
            )}
            {dProvided.placeholder}
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setVisibleCount((v) => v + WALLET_LANE_PAGE * 2)}
                className="w-full mt-1 py-1.5 text-[11px] font-semibold text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border border-dashed border-slate-300 rounded"
                data-testid={`${testId}-show-more`}
              >
                Show more ({hiddenCount} hidden)
              </button>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}

function MechanicLane({
  lane, index, onCardClick, labourRate, onPartsToggle, partsBusyId,
  onStatusChange, statusBusyId, canManage, user, mechanics, onFetchMechanics,
  onUpdatePriority, onUpdateDeliveryDue, onAssignMechanic, onDelete, onCreateInvoice,
  workday, clockBusy, onClockAction, canOverrideClock, columnDragProvided,
  columnDragHandleProps,
}) {
  const hours = lane.cards.reduce((s, c) => s + Number(c.quoted_hours || 0), 0);
  const inProgressCount = lane.cards.filter((c) => c.status === 'in_progress').length;
  const pickupCount = lane.cards.filter((c) => c.status === 'pickup').length;
  const subtitle = inProgressCount || pickupCount
    ? `${inProgressCount} active · ${pickupCount} pickup`
    : 'idle';

  // PERF (iter230) — windowed rendering (see StaticLane). Mechanic lanes are
  // usually small but legacy backlogs can pile 50+ cards on one tech.
  const [visibleCount, setVisibleCount] = React.useState(WALLET_LANE_PAGE);
  const visibleCards = lane.cards.length > visibleCount
    ? lane.cards.slice(0, visibleCount)
    : lane.cards;
  const hiddenCount = lane.cards.length - visibleCards.length;

  return (
    <div
      ref={columnDragProvided?.innerRef}
      {...(columnDragProvided?.draggableProps || {})}
      style={columnDragProvided?.draggableProps?.style}
      className="flex flex-col w-64 shrink-0 rounded-lg border border-blue-200 bg-blue-50/30"
      data-testid={`wallet-col-mechanic-${lane.mechanic.user_id}`}
    >
      <div className="px-3 py-2 border-b border-slate-200 sticky top-0 bg-inherit rounded-t-lg">
        <div className="flex items-center gap-2 mb-0.5">
          {/* Drag handle for column reorder — only visible to SM/FM. The
              entire column lifts when grabbed here; tiles continue to drag
              individually via their own handles. */}
          {canOverrideClock && (
            <span
              {...(columnDragHandleProps || {})}
              data-testid={`wallet-col-drag-${lane.mechanic.user_id}`}
              title="Drag to reorder mechanic columns"
              className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700 shrink-0"
            >
              <GripVertical className="h-4 w-4" />
            </span>
          )}
          <div className="h-7 w-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[11px] shrink-0">
            {initials(lane.mechanic.name)}
          </div>
          <h4
            className="text-sm font-bold text-slate-900 truncate flex-1"
            data-testid={`wallet-mech-name-${lane.mechanic.user_id}`}
          >
            {lane.mechanic.name || 'Unknown'}
          </h4>
          <Badge variant="outline" className="text-[10px] shrink-0">{lane.cards.length}</Badge>
        </div>
        <p className="text-[11px] text-slate-500 truncate">{subtitle}</p>
        {hours > 0 && (
          <p className="text-[10px] font-mono text-slate-600">{hours.toFixed(1)}h quoted</p>
        )}
        {canOverrideClock && (
          <MechanicClockControls
            mechanicId={lane.mechanic.user_id}
            workday={workday}
            busy={clockBusy}
            onAction={onClockAction}
          />
        )}
      </div>
      <Droppable droppableId={dropMechId(lane.mechanic.user_id)} type="WALLET_JC">
        {(dProvided, snap) => (
          <div
            ref={dProvided.innerRef}
            {...dProvided.droppableProps}
            className={`flex-1 p-2 overflow-y-auto max-h-[calc(100vh-280px)] min-h-[200px] ${snap.isDraggingOver ? 'bg-blue-50/60' : ''}`}
          >
            {lane.cards.length === 0 ? (
              <p className="text-[11px] text-slate-400 italic text-center py-3">— drop a job here —</p>
            ) : (
              visibleCards.map((c, idx) => (
                <DraggableTile
                  key={c.job_card_id}
                  card={c}
                  index={idx}
                  draggableId={c.job_card_id}
                  onClick={onCardClick}
                  labourRate={labourRate}
                  onPartsToggle={onPartsToggle}
                  partsBusyId={partsBusyId}
                  onStatusChange={onStatusChange}
                  statusBusyId={statusBusyId}
                  canManage={canManage}
                  user={user}
                  mechanics={mechanics}
                  onFetchMechanics={onFetchMechanics}
                  onUpdatePriority={onUpdatePriority}
                  onUpdateDeliveryDue={onUpdateDeliveryDue}
                  onAssignMechanic={onAssignMechanic}
                  onDelete={onDelete}
                  onCreateInvoice={onCreateInvoice}
                />
              ))
            )}
            {dProvided.placeholder}
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setVisibleCount((v) => v + WALLET_LANE_PAGE * 2)}
                className="w-full mt-1 py-1.5 text-[11px] font-semibold text-slate-600 hover:text-slate-900 bg-white/70 hover:bg-white border border-dashed border-slate-300 rounded"
                data-testid={`wallet-mech-show-more-${lane.mechanic.user_id}`}
              >
                Show more ({hiddenCount} hidden)
              </button>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}

/* ─────────────────────────  DnD orchestration  ─────────────────────── */
async function applyDragOp({ src, dst, draggableId, grouped, mechanics }) {
  const srcId = src.droppableId;
  const dstId = dst.droppableId;
  const dstIndex = dst.index;

  const laneCards = (laneId) => {
    if (laneId === DROP_QUOTE) return grouped.quote;
    if (laneId === DROP_BOOKING) return grouped.booking;
    if (laneId === DROP_PICKUP_UNASSGN) return grouped.pickupUnassigned;
    if (laneId === DROP_WAITING_PARTS) return grouped.waitingParts;
    if (laneId === DROP_SUBLET) return grouped.sublet;
    if (laneId === DROP_BREAK) return grouped.breakLane;
    if (laneId === DROP_REVIEW) return grouped.review;
    if (laneId === DROP_COMPLETED) return grouped.completed;
    const mid = parseMechFromDropId(laneId);
    if (mid) {
      const ln = grouped.lanes.find((l) => l.mechanic.user_id === mid);
      return ln ? ln.cards : [];
    }
    return [];
  };

  if (srcId === dstId) {
    const cards = [...laneCards(srcId)];
    const fromIdx = cards.findIndex((c) => c.job_card_id === draggableId);
    if (fromIdx === -1) return null;
    const [moving] = cards.splice(fromIdx, 1);
    cards.splice(dstIndex, 0, moving);
    const updates = cards.map((c, i) => ({ job_card_id: c.job_card_id, priority_rank: i + 1 }));
    return { kind: 'reorder', updates, toast: `Re-ranked ${cards.length} card(s)` };
  }

  const dstMech = parseMechFromDropId(dstId);

  const movedCard = (() => {
    for (const arr of [
      grouped.quote, grouped.booking, grouped.pickupUnassigned, grouped.waitingParts,
      grouped.sublet, grouped.breakLane, grouped.review, grouped.completed,
    ]) {
      const hit = arr.find((c) => c.job_card_id === draggableId);
      if (hit) return hit;
    }
    for (const l of grouped.lanes) {
      const hit = l.cards.find((c) => c.job_card_id === draggableId);
      if (hit) return hit;
    }
    return null;
  })();
  if (!movedCard) return null;

  if (srcId === DROP_BOOKING) {
    return { kind: 'block', toast: 'Check the customer in first — drag from Pickup, not Booking.' };
  }
  if (srcId === DROP_QUOTE) {
    return { kind: 'block', toast: 'Open the quote tile to Schedule it as a booking — drag won\'t work on Quote tiles.' };
  }

  // Drop INTO a mechanic lane — re-assign + status normalisation.
  if (dstMech) {
    const mechActive = (mechanics || []).some((m) => m.user_id === dstMech);
    if (!mechActive) {
      return { kind: 'block', toast: 'That mechanic is no longer active.' };
    }
    const lane = grouped.lanes.find((l) => l.mechanic.user_id === dstMech);
    const dstCards = lane ? [...lane.cards] : [];
    // Default new status when dropping onto a mechanic lane:
    //  • from booking/no_show/sm_review/completed → in_progress
    //  • from break → in_progress (resume work)
    //  • from any waiting/sublet → in_progress
    //  • otherwise keep current
    let normalised = movedCard.status;
    if (['sm_review', 'completed', 'booking', 'no_show', 'break',
         'waiting_for_parts', 'blocked'].includes(movedCard.status)) {
      normalised = 'in_progress';
    }
    if (srcId === DROP_PICKUP_UNASSGN) normalised = 'pickup';

    const cardClone = { ...movedCard, assigned_to: dstMech, status: normalised };
    dstCards.splice(dstIndex, 0, cardClone);
    const updates = dstCards.map((c, i) => ({
      job_card_id: c.job_card_id,
      priority_rank: i + 1,
      ...(c.job_card_id === draggableId ? { assigned_to: dstMech } : {}),
    }));
    const statusChange = movedCard.status !== normalised ? normalised : null;
    return {
      kind: 'reassign',
      updates,
      statusChange: statusChange ? { jc_id: draggableId, status: statusChange } : null,
      toast: `Assigned to ${(mechanics || []).find((m) => m.user_id === dstMech)?.name || 'mechanic'}`,
    };
  }

  // Drop INTO a status lane — status change.
  const statusMap = {
    [DROP_PICKUP_UNASSGN]: 'pickup',
    [DROP_WAITING_PARTS]: 'waiting_for_parts',
    [DROP_SUBLET]: 'blocked',
    [DROP_BREAK]: 'break',
    [DROP_REVIEW]: 'sm_review',
    [DROP_COMPLETED]: 'completed',
  };
  const newStatus = statusMap[dstId];
  if (newStatus) {
    return {
      kind: 'status',
      jc_id: draggableId,
      status: newStatus,
      clearAssignment: dstId === DROP_PICKUP_UNASSGN,
      toast: `Moved to ${STATUS_META[newStatus]?.label || newStatus}`,
    };
  }

  return null;
}

/* ─────────────────────────  main board  ───────────────────────────── */
export default function MechanicWalletBoard({
  columns, mechanics, onCardClick, onChange, labourRate, canDrag = true,
  onActiveJcConflict,
  user, onFetchMechanics, onUpdatePriority, onUpdateDeliveryDue, onAssignMechanic, onDelete, onCreateInvoice,
  showQuoteColumn = true,
}) {
  const canOverrideClock = ['super_admin', 'service_manager', 'floor_manager', 'admin']
    .includes(user?.role);

  /* Persisted mechanic column order. Saved order takes precedence; new
   * mechanics appear at the end (alphabetical). */
  const [mechOrder, setMechOrder] = useState(() => loadSavedOrder());

  /* iter164 — Optimistic priority-rank overrides for same-lane reorders.
   * Populated by handleDragEnd on successful reorder to avoid a full
   * refetch (which causes visible snap-back lag during rapid dnd). The
   * map is reset whenever the parent supplies a fresh `columns` prop
   * (because the server data is then authoritative for those JCs). */
  const [rankOverrides, setRankOverrides] = useState({});
  const prevColumnsRef = useRef(columns);
  useEffect(() => {
    if (columns !== prevColumnsRef.current) {
      prevColumnsRef.current = columns;
      // Only clear if the columns actually contain data — an intermediate
      // undefined during fetch shouldn't clobber our optimistic overrides.
      if (columns) setRankOverrides({});
    }
  }, [columns]);

  const grouped = useMemo(() => {
    const flat = flattenColumns(columns);
    // iter164 — Apply optimistic rank overrides so same-lane reorders
    // feel instant. The overrides are populated in handleDragEnd when
    // the reorder POST succeeds (we skip the full refetch to eliminate
    // network round-trip lag). They're cleared automatically the next
    // time `columns` refreshes from the parent because the server data
    // will then reflect the new ranks (map entries become no-ops).
    const patched = rankOverrides
      ? flat.map((c) => (
          rankOverrides[c.job_card_id] !== undefined
            ? { ...c, priority_rank: rankOverrides[c.job_card_id] }
            : c
        ))
      : flat;
    return groupCards(patched, mechanics, mechOrder);
  }, [columns, mechanics, mechOrder, rankOverrides]);

  // Keep the saved order in-sync with the actual mechanic set so we don't
  // accidentally save ids that no longer exist.
  useEffect(() => {
    const allIds = (mechanics || []).map((m) => m.user_id);
    const orderedKnown = grouped.lanes.map((l) => l.mechanic.user_id);
    // Persist only if the in-memory order differs from what's saved.
    const sameAsSaved =
      mechOrder.length === orderedKnown.length
      && mechOrder.every((id, i) => id === orderedKnown[i])
      && mechOrder.every((id) => allIds.includes(id));
    if (!sameAsSaved) saveOrder(orderedKnown);
  }, [grouped.lanes, mechanics, mechOrder]);

  const inFlightRef = useRef(false);
  const [errMsg, setErrMsg] = useState(null);
  const [partsBusyId, setPartsBusyId] = useState(null);
  const [statusBusyId, setStatusBusyId] = useState(null);

  /* iter141 — Bulk SMS to Booking column. Set of selected booking JC ids.
     Only booking-column tiles expose checkboxes (per user spec).
     iter149 — Extended: Quote lane also supports bulk SMS. */
  const [selectedBookingIds, setSelectedBookingIds] = useState(new Set());
  const [selectedQuoteIds, setSelectedQuoteIds] = useState(new Set());
  const [bulkSmsOpen, setBulkSmsOpen] = useState(false);
  const [bulkSmsSource, setBulkSmsSource] = useState('booking');
  const { guardComms, commsDialog } = useCommsGate();
  const toggleBookingSelected = useCallback((jcId) => {
    setSelectedBookingIds((prev) => {
      const next = new Set(prev);
      if (next.has(jcId)) next.delete(jcId); else next.add(jcId);
      return next;
    });
  }, []);
  const selectAllBookings = useCallback((bookingItems) => {
    setSelectedBookingIds(new Set((bookingItems || []).map((c) => c.job_card_id)));
  }, []);
  const clearBookingSelection = useCallback(() => {
    setSelectedBookingIds(new Set());
  }, []);
  const toggleQuoteSelected = useCallback((jcId) => {
    setSelectedQuoteIds((prev) => {
      const next = new Set(prev);
      if (next.has(jcId)) next.delete(jcId); else next.add(jcId);
      return next;
    });
  }, []);
  const selectAllQuotes = useCallback((quoteItems) => {
    setSelectedQuoteIds(new Set((quoteItems || []).map((c) => c.job_card_id)));
  }, []);
  const clearQuoteSelection = useCallback(() => {
    setSelectedQuoteIds(new Set());
  }, []);
  const openBulkSmsModal = useCallback((source = 'booking') => {
    const set = source === 'quote' ? selectedQuoteIds : selectedBookingIds;
    if (!set.size) {
      toast.error(`Select at least one ${source === 'quote' ? 'quote' : 'booking'} first`);
      return;
    }
    setBulkSmsSource(source);
    setBulkSmsOpen(true);
  }, [selectedBookingIds, selectedQuoteIds]);

  /* iter149 — Per-lane search + sort state (Wallet parity with Status
     view iter147). Keyed by lane id (quote / booking / pickup /
     sm_review / completed). Persisted in localStorage under
     `wallet-section-ui` so SM's filters/sort survive reloads. */
  const [walletSectionUi, setWalletSectionUi] = useState(() => {
    try {
      const cached = localStorage.getItem('wallet-section-ui');
      if (cached) return JSON.parse(cached);
    } catch { /* noop */ }
    return {};
  });
  useEffect(() => {
    try { localStorage.setItem('wallet-section-ui', JSON.stringify(walletSectionUi)); } catch { /* noop */ }
  }, [walletSectionUi]);
  const laneUiFor = (laneKey) => walletSectionUi[laneKey] || { search: '', sortDir: 'desc' };
  const setLaneSearch = (laneKey, value) =>
    setWalletSectionUi((prev) => ({ ...prev, [laneKey]: { ...laneUiFor(laneKey), search: value } }));
  const toggleLaneSort = (laneKey) =>
    setWalletSectionUi((prev) => ({
      ...prev,
      [laneKey]: { ...laneUiFor(laneKey), sortDir: laneUiFor(laneKey).sortDir === 'asc' ? 'desc' : 'asc' },
    }));

  /* ── Workday state per mechanic (for clock controls) ───────────────── */
  const [workdayMap, setWorkdayMap] = useState({}); // {mechanic_id: workday}
  const [clockBusy, setClockBusy] = useState(false);

  const fetchWorkdays = useCallback(async () => {
    if (!canOverrideClock) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/mechanic/workdays`, { credentials: 'include' });
      if (!r.ok) return;
      const data = await r.json();
      const next = {};
      (data.workdays || []).forEach((wd) => {
        if (wd?.mechanic_id) next[wd.mechanic_id] = wd;
      });
      setWorkdayMap(next);
    } catch (_e) { /* swallow */ }
  }, [canOverrideClock]);

  useEffect(() => {
    fetchWorkdays();
    if (!canOverrideClock) return undefined;
    const id = setInterval(fetchWorkdays, 15000);
    return () => clearInterval(id);
  }, [fetchWorkdays, canOverrideClock]);

  const handleClockAction = async (mechanicId, action) => {
    if (clockBusy) return;
    setClockBusy(true);
    setErrMsg(null);
    try {
      const url = `${BACKEND_URL}/api/mechanic/${action}?target_mechanic_id=${encodeURIComponent(mechanicId)}`;
      const r = await fetch(url, { method: 'POST', credentials: 'include' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const msg = (typeof err?.detail === 'string' ? err.detail : err?.detail?.message)
          || `Clock action failed (${r.status})`;
        toast.error(msg);
        return;
      }
      const data = await r.json();
      if (data.paused_job_card_id) {
        toast.success('On break — JC moved to Break column');
      } else if (data.resumed_job_card_id) {
        toast.success('Resumed — JC back to In Progress');
      } else if (data.closed_job_card_id) {
        toast.success('Clocked off — JC clock closed');
      } else if (data.clocked_in) {
        toast.success('Mechanic clocked in');
      } else if (data.already_open || data.already_off || data.already_on_break) {
        toast.message('Already in that state');
      }
      await fetchWorkdays();
      onChange?.(); // refresh JC kanban so the JC re-renders in its new column
    } catch (_e) {
      toast.error('Network error during clock action');
    } finally {
      setClockBusy(false);
    }
  };

  /* PUT helper that bypasses the global fetch interceptor */
  const xhrPut = (url, payload) => new Promise((resolve) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      try {
        const tok = localStorage.getItem('session_token');
        if (tok) xhr.setRequestHeader('Authorization', `Bearer ${tok}`);
      } catch (_) { /* localStorage blocked → cookie auth still works */ }
      xhr.withCredentials = true;
      xhr.onload = () => {
        let body = null;
        try { body = JSON.parse(xhr.responseText); } catch (_) { /* leave null */ }
        resolve({ status: xhr.status, body, raw: xhr.responseText });
      };
      xhr.onerror = () => resolve({ status: 0, body: null, raw: '' });
      xhr.send(JSON.stringify(payload));
    } catch (_e) {
      resolve({ status: 0, body: null, raw: '' });
    }
  });

  const handlePartsToggle = async (card) => {
    if (!canDrag || partsBusyId) return;
    setPartsBusyId(card.job_card_id);
    setErrMsg(null);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/job-cards/${card.job_card_id}/toggle-parts-ordered`,
        { method: 'POST', credentials: 'include' },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setErrMsg(typeof err?.detail === 'string' ? err.detail : 'Could not update parts status.');
      }
    } catch (_e) {
      setErrMsg('Network error toggling parts.');
    } finally {
      setPartsBusyId(null);
      onChange?.();
    }
  };

  const handleStatusChange = async (card, newStatus) => {
    if (!canDrag || statusBusyId || newStatus === card.status) return;
    setStatusBusyId(card.job_card_id);
    setErrMsg(null);
    try {
      if (newStatus === 'completed') {
        try {
          const pre = await fetch(
            `${BACKEND_URL}/api/job-cards/${card.job_card_id}/can-complete`,
            { credentials: 'include' },
          );
          const pj = await pre.json().catch(() => ({}));
          if (pre.ok && pj && pj.can_complete === false) {
            setErrMsg(`Cannot complete. Missing: ${(pj.missing_items || []).join(', ')}`);
            return;
          }
        } catch (_e) { /* fall through to PUT */ }
      }
      const { status: respStatus, body: err } = await xhrPut(
        `${BACKEND_URL}/api/job-cards/${card.job_card_id}`,
        { status: newStatus },
      );
      if (respStatus < 200 || respStatus >= 300) {
        if (respStatus === 409 && err?.detail?.code === 'MECHANIC_HAS_ACTIVE_JC' && onActiveJcConflict) {
          onActiveJcConflict({
            active: err.detail.active_job_card,
            targetJobCardId: card.job_card_id,
            targetStatus: newStatus,
            message: err.detail.message,
          });
        } else {
          setErrMsg(
            (typeof err?.detail === 'string' ? err.detail : null)
            || err?.detail?.message
            || 'Status update failed.',
          );
        }
      }
    } catch (_e) {
      setErrMsg('Network error updating status.');
    } finally {
      setStatusBusyId(null);
      onChange?.();
    }
  };

  /* ── Column reorder DnD ─────────────────────────────────────────── */
  const handleColumnDragEnd = (result) => {
    if (!result?.destination) return;
    if (result.source.index === result.destination.index) return;
    const ids = grouped.lanes.map((l) => l.mechanic.user_id);
    const [moved] = ids.splice(result.source.index, 1);
    ids.splice(result.destination.index, 0, moved);
    setMechOrder(ids);
    saveOrder(ids);
    toast.success('Column order saved');
  };

  /* ── Tile DnD (status / reassign / reorder) ─────────────────────── */
  const handleDragEnd = async (result) => {
    if (!result?.destination || inFlightRef.current) return;

    // Column reorder uses its own type — branch early.
    if (result.type === 'WALLET_COL') {
      handleColumnDragEnd(result);
      return;
    }

    if (!canDrag) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const op = await applyDragOp({
      src: source, dst: destination, draggableId, grouped, mechanics,
    });
    if (!op) return;

    inFlightRef.current = true;
    setErrMsg(null);
    // iter164 — Track whether a full refetch is actually needed. Same-lane
    // reorders (op.kind === 'reorder' without a status change) only touch
    // priority_rank — they don't change status, assignment, or any other
    // field that affects OTHER JCs. Skipping the refetch (~500ms + full
    // list re-render) makes drag-to-reprioritise feel instant.
    let needsRefetch = true;
    try {
      if (op.kind === 'block') {
        setErrMsg(op.toast);
        return;
      }
      if (op.kind === 'reorder' || op.kind === 'reassign') {
        const r = await fetch(`${BACKEND_URL}/api/job-cards/reorder`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: op.updates }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          // iter138.6 — Surface the structured ALLOCATED_HOURS_REQUIRED
          // error via toast so SM sees it immediately on top of the
          // wallet (not buried in the slim banner above the columns).
          const detail = err?.detail;
          if (detail && typeof detail === 'object' && detail.code === 'ALLOCATED_HOURS_REQUIRED') {
            toast.error(detail.message, { duration: 10000 });
            setErrMsg(detail.message);
          } else {
            setErrMsg(typeof detail === 'string' ? detail : detail?.message || 'Reorder failed.');
          }
        } else if (op.statusChange) {
          const { status: respStatus, body: err } = await xhrPut(
            `${BACKEND_URL}/api/job-cards/${op.statusChange.jc_id}`,
            { status: op.statusChange.status },
          );
          if (respStatus < 200 || respStatus >= 300) {
            if (respStatus === 409 && err?.detail?.code === 'MECHANIC_HAS_ACTIVE_JC' && onActiveJcConflict) {
              onActiveJcConflict({
                active: err.detail.active_job_card,
                targetJobCardId: op.statusChange.jc_id,
                targetStatus: op.statusChange.status,
                message: err.detail.message,
              });
            } else {
              setErrMsg(
                (typeof err?.detail === 'string' ? err.detail : null)
                || err?.detail?.message
                || 'Status update failed.',
              );
            }
          }
        } else if (op.kind === 'reorder' && !op.statusChange) {
          // iter164 — Pure priority reorder within the same lane. The
          // server confirmed the new ranks. Skip the full kanban refetch
          // — dnd-kit already applied the visual reorder optimistically,
          // and no other JC's data changed. Result: drag feels instant.
          // Also PATCH local `rankOverrides` so grouped.lanes re-sorts to
          // the new order on the next render (otherwise the tile would
          // snap back to its old rank the moment React re-renders for
          // any other reason).
          const patch = {};
          for (const u of op.updates) patch[u.job_card_id] = u.priority_rank;
          setRankOverrides((prev) => ({ ...prev, ...patch }));
          needsRefetch = false;
        }
      } else if (op.kind === 'status') {
        const body = { status: op.status };
        if (op.clearAssignment) body.assigned_to = null;
        const { status: respStatus, body: err } = await xhrPut(
          `${BACKEND_URL}/api/job-cards/${op.jc_id}`,
          body,
        );
        if (respStatus < 200 || respStatus >= 300) {
          if (respStatus === 409 && err?.detail?.code === 'MECHANIC_HAS_ACTIVE_JC' && onActiveJcConflict) {
            onActiveJcConflict({
              active: err.detail.active_job_card,
              targetJobCardId: op.jc_id,
              targetStatus: op.status,
              message: err.detail.message,
            });
          } else {
            setErrMsg(
              (typeof err?.detail === 'string' ? err.detail : null)
              || err?.detail?.message
              || 'Update failed.',
            );
          }
        }
      }
    } catch (e) {
      setErrMsg('Network error — please retry.');
    } finally {
      inFlightRef.current = false;
      // iter164 — Skip refetch for same-lane reorders (see needsRefetch
      // block above). All other ops (status, reassign, cross-lane)
      // still refetch because other JCs may have shifted (e.g. a break
      // status flip moves another JC into the Break column).
      if (needsRefetch) onChange?.();
    }
  };

  const totalActive = grouped.lanes.reduce((sum, l) => sum + l.cards.length, 0);
  const totalAll =
    grouped.booking.length + grouped.pickupUnassigned.length
    + totalActive + grouped.waitingParts.length + grouped.sublet.length
    + grouped.breakLane.length + grouped.review.length + grouped.completed.length;

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="space-y-2" data-testid="mechanic-wallet-board">
        <div className="text-xs text-slate-600 px-1 flex items-center gap-3">
          <span>
            Showing <span className="font-bold text-slate-900">{totalAll}</span> job cards across{' '}
            <span className="font-bold text-slate-900">{grouped.lanes.length}</span> mechanics ·{' '}
            <span className="text-slate-500">Workshop-wide view (date filter ignored)</span>
          </span>
          {labourRate > 0 && (
            <span className="text-slate-500">· Labour rate <span className="font-mono">${Number(labourRate).toFixed(0)}/h</span></span>
          )}
          {canOverrideClock && (
            <span className="text-[10px] text-slate-400 italic">
              · Drag <GripVertical className="inline h-3 w-3" /> to reorder mechanic columns
            </span>
          )}
        </div>

        {errMsg && (
          <div className="text-[11px] bg-rose-50 border border-rose-200 text-rose-700 px-2 py-1 rounded">
            {errMsg}
          </div>
        )}

        <div className="flex gap-3 overflow-x-auto pb-2" style={{ minHeight: '60vh' }}>
          {/* ── iter141 — Quote lane (left-most, ALL-TIME, regardless of date filter) ──
              iter164 — Now respects the parent's `showQuoteColumn` toggle
              (the "Quotes" chip at the top of the Job Cards page). SM
              hides it when reviewing floor work so the workshop lanes
              get more horizontal real-estate. */}
          {showQuoteColumn && (
          <StaticLane
            title="Quotes"
            subtitle="All-time · click to schedule"
            icon={FileText}
            accent="border-indigo-200 bg-indigo-50/40"
            dropId={DROP_QUOTE}
            items={grouped.quote}
            emptyLabel="— no open quotes —"
            onCardClick={onCardClick}
            labourRate={labourRate}
            onPartsToggle={handlePartsToggle}
            partsBusyId={partsBusyId}
            onStatusChange={handleStatusChange}
            statusBusyId={statusBusyId}
            canManage={canDrag}
            user={user}
            mechanics={mechanics}
            onFetchMechanics={onFetchMechanics}
            onUpdatePriority={onUpdatePriority}
            onUpdateDeliveryDue={onUpdateDeliveryDue}
            onAssignMechanic={onAssignMechanic}
            onDelete={onDelete}
            onCreateInvoice={onCreateInvoice}
            testId="wallet-col-quote"
            selectableForBulkSms
            selectedIds={selectedQuoteIds}
            onToggleSelect={toggleQuoteSelected}
            onSelectAll={selectAllQuotes}
            onClearSelection={clearQuoteSelection}
            onSendBulkSms={guardComms(() => openBulkSmsModal('quote'))}
            search={laneUiFor('quote').search}
            onSearchChange={(v) => setLaneSearch('quote', v)}
            sortDir={laneUiFor('quote').sortDir}
            onToggleSort={() => toggleLaneSort('quote')}
          />
          )}

          {/* ── Booking ── */}
          <StaticLane
            title="Booking"
            subtitle="Awaiting check-in"
            icon={CalendarClock}
            accent="border-sky-200 bg-sky-50/40"
            dropId={DROP_BOOKING}
            items={grouped.booking}
            emptyLabel="— empty —"
            onCardClick={onCardClick}
            labourRate={labourRate}
            onPartsToggle={handlePartsToggle}
            partsBusyId={partsBusyId}
            onStatusChange={handleStatusChange}
            statusBusyId={statusBusyId}
            canManage={canDrag}
            user={user}
            mechanics={mechanics}
            onFetchMechanics={onFetchMechanics}
            onUpdatePriority={onUpdatePriority}
            onUpdateDeliveryDue={onUpdateDeliveryDue}
            onAssignMechanic={onAssignMechanic}
            onDelete={onDelete}
            onCreateInvoice={onCreateInvoice}
            testId="wallet-col-booking"
            selectableForBulkSms
            selectedIds={selectedBookingIds}
            onToggleSelect={toggleBookingSelected}
            onSelectAll={selectAllBookings}
            onClearSelection={clearBookingSelection}
            onSendBulkSms={guardComms(() => openBulkSmsModal('booking'))}
            search={laneUiFor('booking').search}
            onSearchChange={(v) => setLaneSearch('booking', v)}
            sortDir={laneUiFor('booking').sortDir}
            onToggleSort={() => toggleLaneSort('booking')}
          />

          {/* ── Dropped-off (unassigned) ── */}
          <StaticLane
            title="Dropped-off"
            subtitle="Needs mechanic assignment"
            icon={Inbox}
            accent="border-violet-200 bg-violet-50/40"
            dropId={DROP_PICKUP_UNASSGN}
            items={grouped.pickupUnassigned}
            emptyLabel="— all allocated —"
            onCardClick={onCardClick}
            labourRate={labourRate}
            onPartsToggle={handlePartsToggle}
            partsBusyId={partsBusyId}
            onStatusChange={handleStatusChange}
            statusBusyId={statusBusyId}
            canManage={canDrag}
            user={user}
            mechanics={mechanics}
            onFetchMechanics={onFetchMechanics}
            onUpdatePriority={onUpdatePriority}
            onUpdateDeliveryDue={onUpdateDeliveryDue}
            onAssignMechanic={onAssignMechanic}
            onDelete={onDelete}
            onCreateInvoice={onCreateInvoice}
            testId="wallet-col-pickup-unassigned"
            search={laneUiFor('pickup').search}
            onSearchChange={(v) => setLaneSearch('pickup', v)}
            sortDir={laneUiFor('pickup').sortDir}
            onToggleSort={() => toggleLaneSort('pickup')}
          />

          {/* ── Per-mechanic lanes — horizontally draggable for column reorder ── */}
          <Droppable
            droppableId="wallet-mech-columns"
            type="WALLET_COL"
            direction="horizontal"
          >
            {(dProvided) => (
              <div
                ref={dProvided.innerRef}
                {...dProvided.droppableProps}
                className="flex gap-3"
                data-testid="wallet-mech-columns"
              >
                {grouped.lanes.map((lane, idx) => (
                  <Draggable
                    key={lane.mechanic.user_id}
                    draggableId={`mech-col-${lane.mechanic.user_id}`}
                    index={idx}
                    isDragDisabled={!canOverrideClock}
                  >
                    {(dragProvided) => (
                      <MechanicLane
                        lane={lane}
                        index={idx}
                        onCardClick={onCardClick}
                        labourRate={labourRate}
                        onPartsToggle={handlePartsToggle}
                        partsBusyId={partsBusyId}
                        onStatusChange={handleStatusChange}
                        statusBusyId={statusBusyId}
                        canManage={canDrag}
                        user={user}
                        mechanics={mechanics}
                        onFetchMechanics={onFetchMechanics}
                        onUpdatePriority={onUpdatePriority}
                        onUpdateDeliveryDue={onUpdateDeliveryDue}
                        onAssignMechanic={onAssignMechanic}
                        onDelete={onDelete}
                        onCreateInvoice={onCreateInvoice}
                        workday={workdayMap[lane.mechanic.user_id]}
                        clockBusy={clockBusy}
                        onClockAction={handleClockAction}
                        canOverrideClock={canOverrideClock}
                        columnDragProvided={dragProvided}
                        columnDragHandleProps={dragProvided.dragHandleProps}
                      />
                    )}
                  </Draggable>
                ))}
                {dProvided.placeholder}
              </div>
            )}
          </Droppable>

          {/* ── Waiting for Parts ── */}
          <StaticLane
            title="Waiting for Parts"
            subtitle="Parts on order"
            icon={PauseCircle}
            accent="border-amber-200 bg-amber-50/40"
            dropId={DROP_WAITING_PARTS}
            items={grouped.waitingParts}
            emptyLabel="— empty —"
            onCardClick={onCardClick}
            showMechanic
            mechLookup={grouped.mechLookup}
            labourRate={labourRate}
            onPartsToggle={handlePartsToggle}
            partsBusyId={partsBusyId}
            onStatusChange={handleStatusChange}
            statusBusyId={statusBusyId}
            canManage={canDrag}
            user={user}
            mechanics={mechanics}
            onFetchMechanics={onFetchMechanics}
            onUpdatePriority={onUpdatePriority}
            onUpdateDeliveryDue={onUpdateDeliveryDue}
            onAssignMechanic={onAssignMechanic}
            onDelete={onDelete}
            onCreateInvoice={onCreateInvoice}
            testId="wallet-col-waiting-parts"
          />

          {/* ── Sublet ── */}
          <StaticLane
            title="Sublet"
            subtitle="External / specialist work"
            icon={AlertTriangle}
            accent="border-rose-200 bg-rose-50/40"
            dropId={DROP_SUBLET}
            items={grouped.sublet}
            emptyLabel="— empty —"
            onCardClick={onCardClick}
            showMechanic
            mechLookup={grouped.mechLookup}
            labourRate={labourRate}
            onPartsToggle={handlePartsToggle}
            partsBusyId={partsBusyId}
            onStatusChange={handleStatusChange}
            statusBusyId={statusBusyId}
            canManage={canDrag}
            user={user}
            mechanics={mechanics}
            onFetchMechanics={onFetchMechanics}
            onUpdatePriority={onUpdatePriority}
            onUpdateDeliveryDue={onUpdateDeliveryDue}
            onAssignMechanic={onAssignMechanic}
            onDelete={onDelete}
            onCreateInvoice={onCreateInvoice}
            testId="wallet-col-sublet"
          />

          {/* ── Break ── */}
          <StaticLane
            title="Break"
            subtitle="Mechanic on break — JC paused"
            icon={Coffee}
            accent="border-amber-300 bg-amber-100/40"
            dropId={DROP_BREAK}
            items={grouped.breakLane}
            emptyLabel="— no one on break —"
            onCardClick={onCardClick}
            showMechanic
            mechLookup={grouped.mechLookup}
            labourRate={labourRate}
            onPartsToggle={handlePartsToggle}
            partsBusyId={partsBusyId}
            onStatusChange={handleStatusChange}
            statusBusyId={statusBusyId}
            canManage={canDrag}
            user={user}
            mechanics={mechanics}
            onFetchMechanics={onFetchMechanics}
            onUpdatePriority={onUpdatePriority}
            onUpdateDeliveryDue={onUpdateDeliveryDue}
            onAssignMechanic={onAssignMechanic}
            onDelete={onDelete}
            onCreateInvoice={onCreateInvoice}
            testId="wallet-col-break"
          />

          {/* ── SM Review ── */}
          <StaticLane
            title="SM Review"
            subtitle="Ready for sign-off"
            icon={ClipboardCheck}
            accent="border-indigo-200 bg-indigo-50/40"
            dropId={DROP_REVIEW}
            items={grouped.review}
            emptyLabel="— empty —"
            onCardClick={onCardClick}
            showMechanic
            mechLookup={grouped.mechLookup}
            labourRate={labourRate}
            onPartsToggle={handlePartsToggle}
            partsBusyId={partsBusyId}
            onStatusChange={handleStatusChange}
            statusBusyId={statusBusyId}
            canManage={canDrag}
            user={user}
            mechanics={mechanics}
            onFetchMechanics={onFetchMechanics}
            onUpdatePriority={onUpdatePriority}
            onUpdateDeliveryDue={onUpdateDeliveryDue}
            onAssignMechanic={onAssignMechanic}
            onDelete={onDelete}
            onCreateInvoice={onCreateInvoice}
            testId="wallet-col-sm-review"
            search={laneUiFor('sm_review').search}
            onSearchChange={(v) => setLaneSearch('sm_review', v)}
            sortDir={laneUiFor('sm_review').sortDir}
            onToggleSort={() => toggleLaneSort('sm_review')}
          />

          {/* ── Completed ── */}
          <StaticLane
            title="Completed"
            subtitle="Recent finished work"
            icon={CheckCircle2}
            accent="border-emerald-200 bg-emerald-50/40"
            dropId={DROP_COMPLETED}
            items={grouped.completed}
            emptyLabel="— empty —"
            onCardClick={onCardClick}
            showMechanic
            mechLookup={grouped.mechLookup}
            labourRate={labourRate}
            onPartsToggle={handlePartsToggle}
            partsBusyId={partsBusyId}
            onStatusChange={handleStatusChange}
            statusBusyId={statusBusyId}
            canManage={canDrag}
            user={user}
            mechanics={mechanics}
            onFetchMechanics={onFetchMechanics}
            onUpdatePriority={onUpdatePriority}
            onUpdateDeliveryDue={onUpdateDeliveryDue}
            onAssignMechanic={onAssignMechanic}
            onDelete={onDelete}
            onCreateInvoice={onCreateInvoice}
            testId="wallet-col-completed"
            search={laneUiFor('completed').search}
            onSearchChange={(v) => setLaneSearch('completed', v)}
            sortDir={laneUiFor('completed').sortDir}
            onToggleSort={() => toggleLaneSort('completed')}
          />
        </div>
      </div>

      {commsDialog}
      {/* iter141 + iter149 — Bulk SMS to selected Booking OR Quote lane tiles */}
      <BulkBookingSmsModal
        open={bulkSmsOpen}
        onOpenChange={(v) => {
          setBulkSmsOpen(v);
          if (!v) {
            // Keep the selection after dismissing so SM can re-open; clear
            // it only after a successful send (handled in onSent below).
          }
        }}
        bookingIds={bulkSmsSource === 'quote' ? selectedQuoteIds : selectedBookingIds}
        onSent={() => {
          if (bulkSmsSource === 'quote') clearQuoteSelection();
          else clearBookingSelection();
          // Refresh kanban so the activity log / sms log reflect the send
          onChange?.();
        }}
      />
    </DragDropContext>
  );
}
